'use client';

import { jsx as _jsx } from "react/jsx-runtime";
import { useSearchParams } from 'next/navigation';
import { useLayoutEffect, useRef } from 'react';
import { getValidModal } from './getValidModal.js';
import { useModalManager } from './ModalManagerProvider.js';
const exitModalCallback = (background, onExitComplete) => {
  if (background) {
    background.removeAttribute('aria-hidden');
    background.removeAttribute('inert');
    background.inert = false;
    background.hidden = false;
  }
  onExitComplete();
};
/**
 * Modal util component used along with {@link Modal} to keep the background mounted
 * while preventing interaction with it during the modal view.
 *
 * @param root0 - Component props
 * @param root0.children the children to pass down
 * @param root0.modalTypes the list of available modaltypes to validate
 * @param root0.shouldHideContentWhenModal remove background children from layout while keeping them mounted
 * ```tsx
 * &lt;ModalSidekick modalTypes={['login', 'register']}>
 * // main content remains mounted at its current scroll position
 * &lt;/ModalSidekick>
 * &lt;Modal>
 * // modal content (treated as its "own page")
 * &lt;/Modal>
 * ```
 * @returns Sidekick component.
 */
export default function ModalSidekick({
  children,
  modalTypes,
  shouldHideContentWhenModal = false
}) {
  const searchParams = useSearchParams();
  const {
    enterModalView,
    exitModalView,
    focusModalDialog
  } = useModalManager();
  const hasModal = Boolean(getValidModal(modalTypes, searchParams));
  const backgroundRef = useRef(null);
  // Effect to handle showing modal and updating of background content on modal view mount/unmount.
  useLayoutEffect(() => {
    if (!hasModal) return undefined;
    focusModalDialog();
    const token = enterModalView();
    const background = backgroundRef.current;
    if (background) {
      background.setAttribute('aria-hidden', 'true');
      background.setAttribute('inert', '');
      background.inert = true;
      if (shouldHideContentWhenModal) background.hidden = true;
    }
    return () => {
      exitModalCallback(background, () => {
        exitModalView(token);
      });
    };
  }, [enterModalView, exitModalView, focusModalDialog, hasModal, shouldHideContentWhenModal]);
  return _jsx("div", {
    ref: backgroundRef,
    children: children
  });
}